import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import PriceChart from "../components/PriceChart";
import NewsCard from "../components/NewsCard";
import { ArrowLeft, TrendingUp, TrendingDown, ArrowLeftRight } from "lucide-react";

const PAIR_BIOS = {
  "EUR/USD": "The Euro / US Dollar pair, often called \"Fiber\" by traders, is the most heavily traded currency pair in the world, reflecting the relative economic strength of the Eurozone and the United States.",
  "GBP/USD": "The British Pound / US Dollar pair, nicknamed \"Cable,\" tracks the exchange rate between the UK and US economies and is closely watched around Bank of England and Federal Reserve decisions.",
  "USD/JPY": "The US Dollar / Japanese Yen pair reflects the interest rate differential between the Federal Reserve and the Bank of Japan, and is a key gauge of global risk sentiment.",
  "USD/CHF": "The US Dollar / Swiss Franc pair is often referred to as a \"safe haven\" trade, since the Swiss Franc tends to strengthen during periods of global economic uncertainty.",
  "AUD/USD": "The Australian Dollar / US Dollar pair, known as \"Aussie,\" is heavily influenced by commodity prices and China's economic demand, given Australia's resource-driven economy.",
  "USD/CAD": "The US Dollar / Canadian Dollar pair, called \"Loonie,\" is closely tied to oil prices, given Canada's status as a major crude oil exporter to the United States.",
  "NZD/USD": "The New Zealand Dollar / US Dollar pair, known as \"Kiwi,\" is influenced by New Zealand's agricultural exports and interest rate policy from the Reserve Bank of New Zealand.",
  "EUR/GBP": "The Euro / British Pound pair tracks the exchange rate between the Eurozone and the United Kingdom, and is sensitive to ECB and Bank of England policy divergence.",
  "EUR/JPY": "The Euro / Japanese Yen pair is a popular cross pair reflecting risk sentiment between the Eurozone and Japan, without direct US Dollar exposure.",
  "GBP/JPY": "The British Pound / Japanese Yen pair is known for higher volatility among major crosses, often reacting sharply to UK and Japanese monetary policy shifts.",
  "USD/MXN": "The US Dollar / Mexican Peso pair reflects trade and economic ties between the United States and Mexico, and is sensitive to shifts in US monetary policy and Mexican interest rates.",
  "USD/CNY": "The US Dollar / Chinese Yuan pair tracks the exchange rate managed within a band by the People's Bank of China, making it central to global trade and manufacturing costs.",
  "EUR/CHF": "The Euro / Swiss Franc pair reflects economic sentiment between the Eurozone and Switzerland, with the Franc often strengthening during periods of European uncertainty.",
  "USD/SGD": "The US Dollar / Singapore Dollar pair tracks one of Asia's most stable currencies, closely tied to Singapore's role as a major regional financial and trade hub.",
  "USD/HKD": "The US Dollar / Hong Kong Dollar pair is unique among major pairs since the Hong Kong Dollar is pegged within a narrow band to the US Dollar by the Hong Kong Monetary Authority.",
  "USD/ZAR": "The US Dollar / South African Rand pair is influenced by commodity prices, particularly gold and platinum, given South Africa's status as a major mining exporter.",
  "USD/TRY": "The US Dollar / Turkish Lira pair has experienced significant volatility in recent years, closely tied to Turkey's inflation rate and central bank policy decisions.",
  "EUR/AUD": "The Euro / Australian Dollar pair reflects the relationship between the Eurozone economy and Australia's commodity-driven economy, particularly demand from Asia.",
  "GBP/AUD": "The British Pound / Australian Dollar pair tracks the exchange rate between the UK and Australian economies, both influenced by their respective central bank policies.",
  "USD/INR": "The US Dollar / Indian Rupee pair reflects India's growing role in the global economy, influenced by the Reserve Bank of India's monetary policy and foreign investment flows.",
};

function Converter({ rate, base, quote }) {
  const [amount, setAmount] = useState("1");
  const result = (parseFloat(amount) || 0) * (rate || 0);
  return (
    <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl p-5">
      <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-tertiary)] mb-4">
        {base}/{quote} Converter
      </h3>
      <div className="relative">
        <input
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-lg px-3 py-2.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400 mb-2"
        />
        <span className="absolute right-3 top-3 text-xs font-mono text-[var(--text-tertiary)]">{base}</span>
      </div>
      <div className="flex justify-center py-1">
        <ArrowLeftRight className="w-4 h-4 text-[var(--text-tertiary)]" />
      </div>
      <div className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-lg px-3 py-2.5 text-sm text-[var(--text-primary)] flex items-center justify-between">
        <span>{result.toLocaleString(undefined, { maximumFractionDigits: 4 })}</span>
        <span className="text-xs font-mono text-[var(--text-tertiary)]">{quote}</span>
      </div>
    </div>
  );
}

export default function ForexDetail() {
  const { pairSlug } = useParams();
  const [pair, setPair] = useState(null);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    Promise.all([
      api.get("/forex/pairs/" + pairSlug),
      api.get("/news?limit=50").catch(() => ({ data: [] })),
    ])
      .then(([p, n]) => {
        setPair(p.data);
        const matches = (n.data || [])
          .filter(
            (a) =>
              a.title?.toLowerCase().includes(p.data.base.toLowerCase()) ||
              a.title?.toLowerCase().includes("forex") ||
              a.title?.toLowerCase().includes("dollar")
          )
          .slice(0, 3);
        setNews(matches);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [pairSlug]);

  if (loading) {
    return <div className="pt-32 text-center text-[var(--text-tertiary)] font-mono">Loading...</div>;
  }
  if (error || !pair) {
    return <div className="pt-32 text-center text-[var(--text-tertiary)] font-mono">Currency pair not found.</div>;
  }

  const isUp = pair.change_pct >= 0;

  return (
    <div className="pt-10 pb-20">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Link
          to="/forex"
          className="inline-flex items-center gap-2 text-sm font-mono text-[var(--text-tertiary)] hover:text-yellow-400 transition mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Forex
        </Link>

        <div className="flex items-center gap-4 mb-8">
          <div className="w-14 h-14 rounded-full bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center">
            <span className="text-yellow-400 font-bold text-sm">{pair.base}</span>
          </div>
          <div>
            <h1 className="font-display font-bold text-3xl text-[var(--text-primary)]">{pair.pair}</h1>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="font-display font-bold text-2xl text-[var(--text-primary)]">
                {pair.rate?.toFixed(5)}
              </span>
              {pair.change_pct != null && (
                <span
                  className={
                    "flex items-center gap-1 font-mono text-sm font-bold " +
                    (isUp ? "text-green-400" : "text-red-400")
                  }
                >
                  {isUp ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                  {isUp ? "+" : ""}
                  {pair.change_pct.toFixed(2)}%
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 space-y-6">
            <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl p-6">
              <h2 className="font-display font-bold text-lg text-[var(--text-primary)] mb-4">
                {pair.pair} Price Chart
              </h2>
              <PriceChart endpoint={"/forex/pairs/" + pairSlug + "/history"} />
            </div>

            {PAIR_BIOS[pair.pair] && (
              <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl p-6">
                <h2 className="font-display font-bold text-lg text-[var(--text-primary)] mb-3">
                  About {pair.pair}
                </h2>
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{PAIR_BIOS[pair.pair]}</p>
              </div>
            )}

            {news.length > 0 && (
              <div>
                <h2 className="font-display font-bold text-xl text-[var(--text-primary)] mb-4">
                  <span className="text-yellow-400">/</span> Latest {pair.pair} News
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {news.map((n) => (
                    <NewsCard key={n.id} article={n} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-4 space-y-4">
            <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl p-5 space-y-3.5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">
                  Base Currency
                </span>
                <span className="font-bold text-sm text-[var(--text-primary)]">{pair.base}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">
                  Quote Currency
                </span>
                <span className="font-bold text-sm text-[var(--text-primary)]">{pair.quote}</span>
              </div>
              {pair.yesterday_rate && (
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">
                    Previous Rate
                  </span>
                  <span className="font-bold text-sm text-[var(--text-primary)]">
                    {pair.yesterday_rate.toFixed(5)}
                  </span>
                </div>
              )}
            </div>

            <Converter rate={pair.rate} base={pair.base} quote={pair.quote} />
          </div>
        </div>

        <p className="text-xs text-[var(--text-tertiary)] text-center mt-10">
          Rate sourced from the European Central Bank via Frankfurter. Daily reference rate. Not financial advice.
        </p>
      </div>
    </div>
  );
}